/**
 * Creator Earnings API Client
 * Shared between web and mobile for creator earnings dashboard
 */

import type {
  CreatorPayoutSummary,
  CreatorStatus,
  FollowerEligibilityDetail,
} from '../creatorPayout';
import type { HttpClient } from './httpClient';

// =============================================================================
// Types
// =============================================================================

export interface EarningsData {
  totalEarnedCents: number;
  payouts: CreatorPayoutSummary[];
  totalPayouts?: number;
  hasMore?: boolean;
}

export interface PeriodDetail {
  payout: CreatorPayoutSummary;
  followers: FollowerEligibilityDetail[];
}

export interface FollowerPreview {
  followers: FollowerEligibilityDetail[];
  eligibleCount: number;
  totalCount: number;
}

export interface CreatorEarningsApiClient {
  getStatus: () => Promise<CreatorStatus>;
  getEarnings: (limit?: number, offset?: number) => Promise<EarningsData>;
  getPeriodDetail: (periodLabel: string) => Promise<PeriodDetail>;
  getFollowerPreview: () => Promise<FollowerPreview>;
}

// =============================================================================
// API Client Factory
// =============================================================================

export function createCreatorEarningsApi(client: HttpClient): CreatorEarningsApiClient {
  return {
    async getStatus() {
      return client.get<CreatorStatus>('/api/creator/status');
    },

    async getEarnings(limit = 12, offset = 0) {
      return client.get<EarningsData>('/api/creator/earnings', { limit, offset });
    },

    async getPeriodDetail(periodLabel: string) {
      return client.get<PeriodDetail>(`/api/creator/earnings/${periodLabel}`);
    },

    async getFollowerPreview() {
      return client.get<FollowerPreview>('/api/creator/followers');
    },
  };
}
